import { datadogRum } from '@datadog/browser-rum';
import { POST } from '@carvana/core-http';
import { CORRELATION_ID_HEADER, getOrCreateSessionId } from '@utils/session-id';
import { getApiBaseUrl } from '../base-url-config';

/*
  Frontend client for the spinner damage-location backend calls, colocated with the
  other S&R services (repair-facilities, user-profile, …) and routed through
  `@carvana/core-http` `POST({ authorized: true })` so auth-token injection, standard
  headers, tracing, and request timeouts are handled the same way everywhere.

  upload-damage-images: the on-Confirm call — MyCars.API evaluates the two bullseye
  captures with the LLM (panel detection), uploads them to Document Manager, and returns
  the evaluation + secure links + document ids. It no longer persists anything.

  save-concern-location: called once at the coverage step, after saveuserselections has
  created the ASR. The FE sends the marked-spot detail + the upload evaluation with the
  new asrId; the backend writes the ConcernLocation + Snowflake rows once (ASR id already
  set). Both calls are fire-and-forget and never throw.
*/

export interface UploadDamageImagesRequest {
  // base64 JPEG (with or without a data: prefix) — image 1 / image 2 sent to the model.
  fullImage: string;
  zoomImage?: string;
  // Vehicle + detected panel.
  stockNumber?: number;
  // S&R modal-session correlation key (SarStore.sarSessionId, a UUID) — persisted by the
  // backend so the ConcernLocation row can be linked to the ASR created later in the
  // same session.
  sarSessionId?: string;
  panelId?: string;
  panelName?: string;
  resolvedSide?: 'Driver' | 'Passenger' | null;
  confidence?: string;
  // The model's free-text reasoning for the panel call.
  description?: string;
  // Spinner-derived camera view + turntable angle.
  viewDescription?: string;
  angleDeg?: number | null;
  // The reference 360° frame the capture came from: index + CDN jpg name / URL.
  frameIndex?: number | null;
  referenceFrameName?: string;
  referenceFrameUrl?: string;
  // Where the customer tapped: % of capture container, resize-stable canvas anchor, and
  // the container's pixel size. The full set the eventual DB record / VIPR needs.
  positionPct?: { x: number; y: number };
  canvasFraction?: { x: number; y: number };
  containerSize?: { w: number; h: number };
  // Model round-trip time (ms) + capture timestamp (epoch ms).
  aiTimeMs?: number | null;
  capturedAt?: number;
}

export interface UploadDamageImagesResult {
  // LLM panel evaluation (now server-side).
  panelId: string | null;
  panelName: string | null;
  confidence: string | null;
  description: string | null;
  fullImageUrl: string | null;
  zoomImageUrl: string | null;
  fullDocumentId: string | null;
  zoomDocumentId: string | null;
  aiTimeMs: number | null;
}

// Everything the coverage step needs to persist the selection once the asrId exists.
// Assembled at Confirm from the marked-spot detail + the upload evaluation, then sent to
// save-concern-location with the afterSaleRepairId.
export interface SaveConcernLocationRequest {
  afterSaleRepairId: number;
  sarSessionId?: string;
  stockNumber?: number;
  panelId?: string | null;
  panelName?: string | null;
  resolvedSide?: 'Driver' | 'Passenger' | null;
  confidence?: string | null;
  description?: string | null;
  viewDescription?: string;
  angleDeg?: number | null;
  frameIndex?: number | null;
  referenceFrameName?: string;
  referenceFrameUrl?: string;
  positionPct?: { x: number; y: number };
  canvasFraction?: { x: number; y: number };
  containerSize?: { w: number; h: number };
  fullImageUrl?: string | null;
  zoomImageUrl?: string | null;
  fullDocumentId?: string | null;
  zoomDocumentId?: string | null;
  aiTimeMs?: number | null;
  capturedAt?: number;
}

const UPLOAD_PATH = '/core/mycars/api/v1/serviceandrepair/upload-damage-images';
const SAVE_PATH = '/core/mycars/api/v1/serviceandrepair/save-concern-location';
const UPLOAD_DD_ACTION = 'sar-upload-damage-images';
const SAVE_DD_ACTION = 'sar-save-concern-location';
// The upload runs a server-side LLM eval + Document Manager upload, so allow more headroom
// than a normal call; the save call is a quick DB write.
const UPLOAD_TIMEOUT_MS = 30000;
const SAVE_TIMEOUT_MS = 15000;
// The app generates fixed-dimension JPEGs (FULL_W=1152 @ q=0.82), so a well-formed payload
// is a few hundred KB. Reject anything an order of magnitude larger before it hits the wire
// (defense-in-depth; the backend enforces the authoritative content-type/size limits).
const MAX_IMAGE_DATA_URL_CHARS = 8_000_000;

const EMPTY: UploadDamageImagesResult = {
  panelId: null,
  panelName: null,
  confidence: null,
  description: null,
  fullImageUrl: null,
  zoomImageUrl: null,
  fullDocumentId: null,
  zoomDocumentId: null,
  aiTimeMs: null,
};

// The capture pipeline only ever produces JPEG data URLs; anything else means the capture
// failed or was tampered with, so we don't ship it.
function isValidImagePayload(image: string | undefined): boolean {
  if (!image) return false;
  if (!image.startsWith('data:image/jpeg')) return false;
  return image.length <= MAX_IMAGE_DATA_URL_CHARS;
}

// Uploads the two images and returns their Document Manager URLs + the panel evaluation.
// Never throws — on any failure it resolves to EMPTY so Confirm still advances the flow and
// the clickstream event still fires (with null URLs flagged) rather than blocking the customer.
export async function uploadDamageImages(req: UploadDamageImagesRequest): Promise<UploadDamageImagesResult> {
  const startTime = Date.now();
  try {
    if (!isValidImagePayload(req.fullImage)) {
      datadogRum.addAction(UPLOAD_DD_ACTION, { type: 'error', reason: 'invalid-image-payload' });
      return EMPTY;
    }
    datadogRum.addAction(UPLOAD_DD_ACTION, { type: 'start' });

    const response = await POST(`${getApiBaseUrl()}${UPLOAD_PATH}`, req, {
      authorized: true,
      headers: { [CORRELATION_ID_HEADER]: getOrCreateSessionId() },
      timeout: UPLOAD_TIMEOUT_MS,
    });

    if (!response.ok) {
      datadogRum.addAction(UPLOAD_DD_ACTION, {
        type: 'error',
        status: response.status,
        elapsedMs: Date.now() - startTime,
      });
      return EMPTY;
    }

    const data = await response.json();
    const result: UploadDamageImagesResult = {
      panelId: data?.panelId ?? null,
      panelName: data?.panelName ?? null,
      confidence: data?.confidence ?? null,
      description: data?.description ?? null,
      fullImageUrl: data?.fullImageUrl ?? null,
      zoomImageUrl: data?.zoomImageUrl ?? null,
      fullDocumentId: data?.fullDocumentId ?? null,
      zoomDocumentId: data?.zoomDocumentId ?? null,
      aiTimeMs: data?.aiTimeMs ?? null,
    };
    datadogRum.addAction(UPLOAD_DD_ACTION, {
      type: 'success',
      hasFullUrl: !!result.fullImageUrl,
      hasZoomUrl: !!result.zoomImageUrl,
      hasPanel: !!result.panelName,
      elapsedMs: Date.now() - startTime,
    });
    return result;
  } catch (err) {
    datadogRum.addAction(UPLOAD_DD_ACTION, {
      type: 'error',
      error: err instanceof Error ? err.message : String(err),
      elapsedMs: Date.now() - startTime,
    });
    return EMPTY;
  }
}

// Persists the confirmed spinner selection once, with the ASR id set. Called
// fire-and-forget at the coverage step after saveuserselections returns the new asrId.
// Never throws — a failure just means no row (the funnel/backup clickstream still has it).
export async function saveConcernLocation(req: SaveConcernLocationRequest): Promise<boolean> {
  const startTime = Date.now();
  try {
    const response = await POST(`${getApiBaseUrl()}${SAVE_PATH}`, req, {
      authorized: true,
      headers: { [CORRELATION_ID_HEADER]: getOrCreateSessionId() },
      timeout: SAVE_TIMEOUT_MS,
    });

    const saved = response.ok ? !!(await response.json())?.saved : false;
    datadogRum.addAction(SAVE_DD_ACTION, {
      type: saved ? 'success' : 'error',
      status: response.status,
      afterSaleRepairId: req.afterSaleRepairId,
      hasPanel: !!req.panelName,
      elapsedMs: Date.now() - startTime,
    });
    return saved;
  } catch (err) {
    datadogRum.addAction(SAVE_DD_ACTION, {
      type: 'error',
      error: err instanceof Error ? err.message : String(err),
      afterSaleRepairId: req.afterSaleRepairId,
      elapsedMs: Date.now() - startTime,
    });
    return false;
  }
}
